import { Path } from '@common/data-models/complex-types/path';
import { ViewStateDelay } from '@common/data-models/navigation/viewstate-delay';
import { ViewStateFilter } from '@common/data-models/navigation/viewstate-filter';
import { ViewStateOptions } from '@common/data-models/navigation/viewstate-options';
import { ViewStateSequence } from '@common/data-models/navigation/viewstate-sequence';
import { ExternalApiData } from '@common/data-models/service/external-api-data';
import { objectTypeReverseMapping, ObjectTypes } from '@common/enums/object-types';
import { ICustomCardFieldsService } from '@common/interfaces/i-custom-card-fields-service';
import { IFieldMappingService } from '@common/interfaces/i-field-mapping-service';
import { IGlobalTablesService } from '@common/interfaces/i-global-tables-service';
import { INomWebServicesJson } from '@common/interfaces/i-nom-web-services-json';
import { IVisualStateService } from '@common/interfaces/i-visual-state-service';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { Util } from '@common/utils/util';
import { Address } from '@src/suite/common/data-models/complex-types/address';
import { AttachmentList } from '@src/suite/common/data-models/complex-types/attachment-list';
import { DateRange } from '@src/suite/common/data-models/complex-types/date-range';
import { DateTime } from '@src/suite/common/data-models/complex-types/date-time';
import { LookupList } from '@src/suite/common/data-models/complex-types/lookup-list';
import { MultiValue } from '@src/suite/common/data-models/complex-types/multi-value';
import { Relationship } from '@src/suite/common/data-models/complex-types/relationship';
import { Table } from '@src/suite/common/data-models/complex-types/table';
import { CriteriaObj } from '@src/suite/common/data-models/criteria/criteria-obj';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
// import { isCollectionType } from '@src/suite/common/utils/record-utils';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

export class Record {
    public static globalTablesService: IGlobalTablesService;
    public static nomWebServicesJson: INomWebServicesJson;
    public static visualStateService: IVisualStateService;
    public static customCardFieldsService: ICustomCardFieldsService;
    public static fieldMappingService: IFieldMappingService;
    public static visibilityCriteriaCache: { [id: string]: CriteriaObj } = {};
    public static visibilityCriteriaReverseCache: { [id: string]: Array<any> } = {};
    public static readOnlyCriteriaCache: { [id: string]: CriteriaObj } = {};
    public static readOnlyCriteriaReverseCache: { [id: string]: Array<any> } = {};
    public static allPropertiesCriteriaCache: { [id: number]: { [id: string]: FieldDef } } = {};
    public static allActionsCriteriaCache: { [id: string]: ExternalAction } = {};
    public static timeoutQueue: TimeoutQueue;
    public static adornerVisibilityCriteriaCache: { [id: string]: Array<{ visibilityCriteria: CriteriaObj; adorner: Adorner }> } = {};

    public isAdded: boolean = false;

    // Generic data manipulation
    // Data container
    public data: { [id: string]: any } = {};
    // Key presence map
    private hasKey: { [id: string]: boolean } = {};
    public parentRecord: any = null;
    public parentChangedSub: Subscription;
    public parentChanged(): void {}
    public links: Array<Record> = [];
    totalCostSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    totalCostObserver = this.totalCostSubject.asObservable();
    public changeSubject = new Subject<void>();
    public changeObserver = this.changeSubject.asObservable();
    public visibilityCriteria: { [id: string]: boolean } = {};
    public readOnlyCriteria: { [id: string]: boolean } = {};
    public visibilityCriteriaGroups: { [id: string]: { [id: string]: FieldDef } } = {};
    public requiredFieldsGroups: { [id: string]: boolean } = {};

    public isPopulated: boolean = false;
    public isRequiredFieldsPopulated: boolean = true;

    public constructor() {
        this.type = ObjectTypes.UNASSIGNED;

        this.initialize();
        this.modTime = new DateTime();
        this.modTime.set(new Date());
        this.relationships = new Relationship();
        this.createdBy = new MultiValue<string>();
    }

    public initialize() {
        const excluded = this.getExcludedFields();
        for (const key in Record.allPropertiesCriteriaCache[this.type]) {
            const prop = Record.allPropertiesCriteriaCache[this.type][key];
            if (!excluded[prop.ID]) {
                this.visibilityCriteria[prop.ID] = true;
                this.readOnlyCriteria[prop.ID] = false;
                let group = prop.FieldGroup;
                if (prop.FieldGroup.toUpperCase() === '' || prop.FieldGroup.toUpperCase() === Record.globalTablesService.PropertiesGroupKey) {
                    group = '';
                }
                if (!this.visibilityCriteriaGroups[group]) this.visibilityCriteriaGroups[group] = {};
                this.visibilityCriteriaGroups[group][prop.ID] = prop;
            }
        }

        for (const key in Record.allActionsCriteriaCache) {
            const action = Record.allActionsCriteriaCache[key];
            if ((action.TopicAction || action.AssignmentAction || action.ScriptAction) && !action.VisibilityCriteria) {
                this.visibilityCriteria[action.ID] = true;
            }
            if (!action.TopicAction && !action.AssignmentAction && !action.ScriptAction) {
                this.visibilityCriteria[action.ID] = false;
            }
        }
    }

    public setupRequiredFieldsGroups(type: string) {
        const groups = Record.globalTablesService.getFieldGroupsFor(type);
        for (const group of groups) {
            this.requiredFieldsGroups[group] = true;
            // Make sure cache is populated
            Record.globalTablesService.getDynamicPropertiesFor(type, group);
        }
        const specials = Record.globalTablesService.getSpecialPropertiesFor(type);
        for (const special of specials) {
            this.requiredFieldsGroups[special.ID] = true;
        }
    }

    protected getExcludedFields(): { [id: string]: boolean } {
        return {};
    }

    public get keys(): Array<string> {
        return Object.keys(this.data);
    }

    public getDataTransferString() {
        const ret = new Array<{ FieldName: string; FieldValue: any }>();
        ret.push({ FieldName: 'Database', FieldValue: this.database });
        ret.push({ FieldName: 'Path', FieldValue: this.path ? this.path.fullPath : '' });
        ret.push({ FieldName: 'Guid', FieldValue: this.guid });
        ret.push({ FieldName: 'Type', FieldValue: this.type });
        return JSON.stringify(ret);
    }

    public getAs<T>(field: string, defaultValue?: T, dontSet?: boolean): T {
        // Typed get
        if (!dontSet && (!this.hasKey[field] || typeof this.data[field] === 'undefined') && defaultValue) {
            this.set(field, defaultValue);
        }
        return this.data[field];
    }

    public get(field: string, defaultValue?: any): any {
        return this.getAs<any>(field, defaultValue);
    }

    public containsKey(field: string): boolean {
        return this.hasKey[field];
    }

    public set(field: string, value: any) {
        const fields: { [id: string]: any } = {};
        fields[field] = value;
        this.setFields(fields);
    }

    private _hasNewChanges: boolean = false;
    private _changesTimeout: any = null;
    public setFields(fields: { [id: string]: any }) {
        this._hasNewChanges = false;
        if (!this.isPopulated && this.type && this.type !== ObjectTypes.UNASSIGNED) {
            for (const prop in Record.allPropertiesCriteriaCache[this.type]) {
                this.updateVisibility(prop);
                this.updateReadOnlyCriteria(prop);
            }
            this.isPopulated = true;
        }

        for (const field in fields) {
            // TODO: slug splitting
            if (field === 'Title') {
                this.set('Slug', fields[field]);
            }
            this.hasKey[field] = true;
            let v = fields[field];
            if (typeof v === 'string') v = v.replace(/[\u00A0]/g, ' ');
            if (this.data[field] && this.data[field].equals) {
                if (!this.data[field].equals(v)) this._hasNewChanges = true;
            } else if (v && v.equals) {
                if (!v.equals(this.data[field])) this._hasNewChanges = true;
            } else if (this.data[field] !== v) this._hasNewChanges = true;
            if (Record.globalTablesService?.isCostFieldType(field)) {
                this.data[field] = v;
                const casted = this.data[field] as Table;
                this._hasNewChanges = true;
                casted.calculateCost(Record.globalTablesService.getListColumnsForSpecialField(field));
                this.totalCostSubject.next(this.calculateCost());
                if (this.parentRecord) this.parentRecord.totalCostSubject.next(this.parentRecord.calculateCost());
            }
            if (this.data[field] instanceof Table) {
                this.data[field] = v;
                this._hasNewChanges = true;
            } else if (this.data[field] instanceof Address) {
                (<Address>this.data[field]).updateFrom(<Address>v);
                this._hasNewChanges = true;
            } else if (this.data[field] instanceof DateTime) {
                this.data[field] = v;
                this._hasNewChanges = true;
            } else {
                this.data[field] = v;
                this._hasNewChanges = true;
            }
            this.updateVisibility(field);
            this.updateReadOnlyCriteria(field);
        }

        if (!this._changesTimeout && this._hasNewChanges) {
            this._changesTimeout = Record.timeoutQueue?.setTimeout(() => {
                this.checkRequiredProperties();
                if (this.parentRecord) this.parentRecord.checkRequiredProperties();
                this._changesTimeout = null;
                if (this.changeSubject) {
                    this.changeSubject.next();
                }
                if (this.parentRecord && this.parentRecord.collectionChangeSubject) {
                    this.parentRecord.scheduleChangeNotifier();
                }
            }, 0);
        }
    }

    public getExternalApiData(actionName: string, userId: string): ExternalApiData {
        return new ExternalApiData(actionName, this.database, this.path.fullPath, this.guid, userId);
    }

    public getRecordAsObject(): { [id: string]: any } {
        const recObj: { [id: string]: any } = {};
        for (const key in this.data) {
            recObj[key] = this.data[key];
        }
        return recObj;
    }

    private resetPropertyGroup() {
        for (const group in this.requiredFieldsGroups) {
            this.requiredFieldsGroups[group] = true;
        }
    }

    get getRequiredProperties(): { [id: string]: FieldDef } {
        return {};
    }

    public checkRequiredProperties() {
        this.resetPropertyGroup();
        this.isRequiredFieldsPopulated = true;

        // Slug is always required
        if (!this.slug || !this.slug.trim()) {
            this.isRequiredFieldsPopulated = false;
        }
        if (Record.globalTablesService.EnforceRequiredFieldsOnCreate) {
            const fields: { [id: string]: FieldDef } = this.getRequiredProperties;

            for (const field in fields) {
                const populated = this.checkIfValueIsPopulated(field);

                if (!populated) {
                    this.isRequiredFieldsPopulated = false;

                    const item: FieldDef = Record.globalTablesService.FieldDefsDict[field];
                    if (item && (item.Validation.toUpperCase() === 'SPECIAL' || item.Validation.toUpperCase() === 'COST')) {
                        this.requiredFieldsGroups[item.ID] = false;
                    } else {
                        let group = Record.globalTablesService.propertiesGroupCache[field];
                        if (typeof group !== 'undefined') {
                            if (!group) group = '';
                            this.requiredFieldsGroups[group] = false;
                        }
                    }
                }
            }
        }
    }

    public checkIfValueIsPopulated(key: any): boolean {
        const v = this.data[key];

        if (typeof this.visibilityCriteria[key] !== 'undefined' && !this.visibilityCriteria[key]) {
            return true;
        }

        if (!v || v.length === 0) {
            return false;
        }
        if (typeof v === 'string') {
            if (v.trim().length === 0) {
                return false;
            }
        }
        if (v instanceof LookupList) {
            if (!v.hasValues()) {
                return false;
            }
        }
        if (v instanceof MultiValue) {
            if (!v.hasValues()) {
                return false;
            }
        }
        if (v instanceof DateTime) {
            if (!v.get() || v.get().getUTCFullYear() === 1800) {
                return false;
            }
        }
        if (v instanceof Address) {
            if (!v.lat || !v.long) {
                return false;
            }
        }
        if (v instanceof Table) {
            if (!v.hasValues()) {
                return false;
            }
        }
        return true;
    }

    public updateVisibility(property: string) {
        let visible = false;
        const propsToUpdate = Record.visibilityCriteriaReverseCache[property];
        if (propsToUpdate) {
            const rec = this.getRecordAsObject();
            for (const prop of propsToUpdate) {
                if (Record.visibilityCriteriaCache[prop]) {
                    try {
                        if (Object.keys(rec).length > 0) {
                            visible = eval(Record.visibilityCriteriaCache[prop].criteria);
                        }
                    } catch (e) {
                        visible = false;
                        // console.log("Visibility Criteria for field " + prop + " is not a valid expression.")
                    }
                    if (!Record.visibilityCriteriaCache[prop].hasPropForObjectType[this.type]) {
                        visible = true;
                    }
                } else {
                    visible = true;
                }
                this.visibilityCriteria[prop] = visible;
                if (Record.allPropertiesCriteriaCache[this.type] && Record.allPropertiesCriteriaCache[this.type][prop]) {
                    let propGroup = Record.allPropertiesCriteriaCache[this.type][prop].FieldGroup;
                    if (propGroup.trim() === '' || propGroup.trim().toUpperCase() === Record.globalTablesService.PropertiesGroupKey) propGroup = '';
                    if (visible) {
                        if (!this.visibilityCriteriaGroups[propGroup]) this.visibilityCriteriaGroups[propGroup] = {};
                        this.visibilityCriteriaGroups[propGroup][prop] = Record.allPropertiesCriteriaCache[this.type][prop];
                    } else if (this.visibilityCriteriaGroups[propGroup]) {
                        delete this.visibilityCriteriaGroups[propGroup][prop];
                        if (Object.keys(this.visibilityCriteriaGroups[propGroup]).length === 0) {
                            delete this.visibilityCriteriaGroups[propGroup];
                        }
                    }
                }
            }
        }
    }

    public updateReadOnlyCriteria(property: string) {
        let visible = false;
        const propsToUpdate = Record.readOnlyCriteriaReverseCache[property];
        if (propsToUpdate) {
            const rec = this.getRecordAsObject();
            for (const prop of propsToUpdate) {
                if (Record.readOnlyCriteriaCache[prop]) {
                    try {
                        if (Object.keys(rec).length > 0) {
                            visible = eval(Record.readOnlyCriteriaCache[prop].criteria);
                        }
                    } catch (e) {
                        visible = false;
                        // console.log("Visibility Criteria for field " + prop + " is not a valid expression.")
                    }
                    if (!Record.readOnlyCriteriaCache[prop].hasPropForObjectType[this.type]) {
                        visible = true;
                    }
                } else {
                    visible = true;
                }
                this.readOnlyCriteria[prop] = visible;
            }
        }
    }

    public copyDataTo(target: Record, preserveGuid?: boolean, fieldsToSkip?: { [field: string]: boolean }, copyParentRef: boolean = false) {
        for (const key in this.data) {
            if ((!preserveGuid && key.toLowerCase() === 'guid') || (fieldsToSkip && fieldsToSkip[key.toLowerCase()])) continue;
            // i++;
            if (this.data[key] == null) continue;

            const val = this.data[key];

            if (val instanceof MultiValue) {
                target.set(key, <MultiValue<any>>val.deepCopy());
            } else if (val instanceof Address) {
                target.set(key, <Address>val.deepCopy());
            } else if (val instanceof AttachmentList) {
                target.set(key, <AttachmentList>val.deepCopy());
            } else if (val instanceof DateRange) {
                target.set(key, <DateRange>val.deepCopy());
            } else if (val instanceof DateTime) {
                target.set(key, <DateTime>val.deepCopy());
            } else if (val instanceof LookupList) {
                target.set(key, <LookupList>val.deepCopy());
            } else if (val instanceof Table) {
                target.set(key, <Table>val.deepCopy());
            } else if (val instanceof ViewStateFilter) {
                target.set(key, <ViewStateFilter>val.deepCopy());
            } else if (val instanceof ViewStateOptions) {
                target.set(key, <ViewStateOptions>val.deepCopy());
            } else if (val instanceof ViewStateDelay) {
                target.set(key, <ViewStateDelay>val.deepCopy());
            } else if (val instanceof ViewStateSequence) {
                target.set(key, <ViewStateSequence>val.deepCopy());
            } else if (val === null || this.isPrimitive(val) || val instanceof Date) {
                // (typeof val !== "object")
                // Primitive types
                target.set(key, val);
            } else if (val instanceof Path) {
                target.set(key, <Path>val.deepCopy());
            } else if (Array.isArray(val)) {
                target.set(key, Util.deepCopy(val));
            } else if (!this.isPrimitive(val)) {
                // Object types
                target.set(key, Util.deepCopy(val));
            }
        }

        // copy visibility criterira
        target.visibilityCriteria = {};
        for (const id in this.visibilityCriteria) {
            target.visibilityCriteria[id] = this.visibilityCriteria[id];
        }

        target.visibilityCriteriaGroups = {};
        for (const id in this.visibilityCriteriaGroups) {
            target.visibilityCriteriaGroups[id] = {};
            for (const fieldGroupId in this.visibilityCriteriaGroups[id]) {
                target.visibilityCriteriaGroups[id][fieldGroupId] = this.visibilityCriteriaGroups[id][fieldGroupId];
            }
        }

        if (copyParentRef) {
            target.parentRecord = this.parentRecord;
        }

        // copy publication view state, specifically publication time
        // if (target instanceof PublicationViewStateRecord && this instanceof PublicationViewStateRecord) {
        //     target.publicationTime = this.publicationTime.deepCopy();
        // }
    }

    public isPrimitive(val) {
        return val !== Object(val);
    }

    public calculateCost(): number {
        const props = Record.globalTablesService.getCostPropertiesFor(objectTypeReverseMapping[this.type]);
        return props.reduce((acc, prop) => {
            const val = this.getAs<Table>(prop.ID);
            if (val) {
                return acc + val.totalCost;
            }
            return acc;
        }, 0);
    }

    public get database(): string {
        return this.getAs<string>('Database');
    }
    public set database(value: string) {
        this.set('Database', value);
    }

    public get path(): Path {
        return this.getAs<Path>('Path');
    }
    public set path(value: Path) {
        this.set('Path', value);
    }
    public set fullPath(value: string) {
        this.set('Path', new Path(value));
    }

    public get guid(): string {
        return this.getAs<string>('Guid');
    }
    public set guid(value: string) {
        this.set('Guid', value);
    }

    public get containerTitle(): string {
        return this.getAs<string>('ContainerTitle');
    }
    public set containerTitle(value: string) {
        this.set('ContainerTitle', value);
    }

    public get float(): boolean {
        return this.getAs<boolean>('Float');
    }
    public set float(value: boolean) {
        this.set('Float', value);
    }

    public get cBreak(): boolean {
        return this.getAs<boolean>('CBreak');
    }
    public set cBreak(value: boolean) {
        this.set('CBreak', value);
    }

    public get title(): string {
        return this.getAs<string>('Title');
    }
    public set title(value: string) {
        this.set('Title', value);
    }

    public get type(): number {
        return this.getAs<number>('Type');
    }
    public set type(value: number) {
        this.set('Type', value);
    }

    public get isCollection(): boolean {
        return false;
        // return isCollectionType(this.type);
    }

    public get priority(): number {
        return this.getAs<number>('Priority');
    }
    public set priority(value: number) {
        this.set('Priority', value);
    }

    public get approved(): boolean {
        return this.getAs<boolean>('Approved');
    }
    public set approved(value: boolean) {
        this.set('Approved', value);
    }

    public get status(): string {
        return this.getAs<string>('status');
    }
    public set status(value: string) {
        this.set('Status', value);
    }

    public get creator(): string {
        return this.getAs<string>('Creator');
    }
    public set creator(value: string) {
        this.set('Creator', value);
    }

    public get createdBy(): MultiValue<string> {
        return this.getAs<MultiValue<string>>('CreatedBy');
    }
    public set createdBy(value: MultiValue<string>) {
        this.set('CreatedBy', value);
    }

    public get createdTime(): DateTime {
        return this.getAs<DateTime>('CreatedDateTime');
    }
    public set createdTime(value: DateTime) {
        this.set('CreatedDateTime', value);
    }

    public get modBy(): string {
        return this.getAs<string>('ModBy');
    }
    public set modBy(value: string) {
        this.set('ModBy', value);
    }

    public get mosObjId(): string {
        return this.getAs<string>('MOSObjId');
    }
    public set mosObjId(value: string) {
        this.set('MOSObjId', value);
    }

    public get mosControl(): boolean {
        return this.getAs<boolean>('MOSControl');
    }
    public set mosControl(value: boolean) {
        this.set('MOSControl', value);
    }

    public get modTime(): DateTime {
        return this.getAs<DateTime>('ModTime');
    }
    public set modTime(value: DateTime) {
        this.set('ModTime', value);
    }

    public get text(): string {
        return this.getAs<string>('Text');
    }
    public set text(value: string) {
        this.set('Text', value);
    }

    public get expires(): DateTime {
        return this.getAs<DateTime>('Expires');
    }
    public set expires(value: DateTime) {
        this.set('Expires', value);
    }

    public get owner(): string {
        return this.getAs<string>('Owner');
    }
    public set owner(value: string) {
        this.set('Owner', value);
    }

    public get beginText(): string {
        return this.getAs<string>('BeginText');
    }
    public set beginText(value: string) {
        this.set('BeginText', value);
    }

    public get slug(): string {
        return this.getAs<string>('Slug', '');
    }

    public get isForPrompterOutput(): boolean {
        return this.getAs<boolean>('IsForPrompterOutput');
    }
    public set isForPrompterOutput(value: boolean) {
        this.set('IsForPrompterOutput', value);
    }

    public get hasText(): boolean {
        return this.getAs<boolean>('HasText');
    }
    public set hasText(value: boolean) {
        this.set('HasText', value);
    }

    public get legalBlock(): boolean {
        return this.getAs<boolean>('LegalBlock');
    }
    public set legalBlock(value: boolean) {
        this.set('LegalBlock', value);
    }

    public get lockBroken(): boolean {
        return this.getAs<boolean>('LockBroken');
    }
    public set lockBroken(value: boolean) {
        this.set('LockBroken', value);
    }

    public get storyLogPreview(): string {
        return this.getAs<string>('StoryLogPreview');
    }
    public set storyLogPreview(value: string) {
        this.set('StoryLogPreview', value);
    }

    public get eventStart(): DateTime {
        return this.getAs<DateTime>('EventStart');
    }
    public set eventStart(value: DateTime) {
        this.set('EventStart', value);
    }

    public get eventEnd(): DateTime {
        return this.getAs<DateTime>('EventEnd');
    }
    public set eventEnd(value: DateTime) {
        this.set('EventEnd', value);
    }

    public get allDayEvent(): boolean {
        return this.getAs<boolean>('AllDayEvent');
    }
    public set allDayEvent(value: boolean) {
        this.set('AllDayEvent', value);
    }

    public get rawTemplate(): string {
        return this.getAs<string>('RawTemplate');
    }
    public set rawTemplate(value: string) {
        this.set('RawTemplate', value);
    }

    public get scriptOrder(): number {
        return this.getAs<number>('ScriptOrder');
    }
    public set scriptOrder(value: number) {
        this.set('ScriptOrder', value);
    }

    public get rawData(): { [id: string]: any } {
        return this.data;
    }

    public get relationships(): Relationship {
        return this.getAs<Relationship>(PSTFieldNames.Relationships);
    }
    public set relationships(value: Relationship) {
        this.set(PSTFieldNames.Relationships, value);
    }

    public get attachments(): AttachmentList {
        return this.getAs<AttachmentList>(PSTFieldNames.PSTExternalFiles);
    }
    public set attachments(value: AttachmentList) {
        this.set(PSTFieldNames.PSTExternalFiles, value);
    }

    public get storyOwner(): MultiValue<string> {
        return null;
    }
    public set storyOwner(value: MultiValue<string>) {}

    public get getSecurityPrivsWrite(): boolean {
        return true;
    }

    public getCostPrivsWrite(department: MultiValue<string>): boolean {
        const desk = department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].cost.write) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.write;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.write;
    }

    public getCostPrivsRead(department: MultiValue<string>): boolean {
        const desk = department;
        let ret = false;
        if (desk.length > 0) {
            for (const val of desk) {
                if (Record.globalTablesService.securityPrivileges[val]) {
                    if (Record.globalTablesService.securityPrivileges[val].cost.read) {
                        ret = true;
                    }
                } else {
                    ret = Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.read;
                }
            }
            return ret;
        }
        return Record.globalTablesService.securityPrivileges[Record.nomWebServicesJson.PSTGroup].cost.read;
    }

    public get fieldDefsDict(): { [id: string]: FieldDef } {
        return null;
    }
}
